import { jsPDF } from 'jspdf';

import {
  CuripodElement,
  CuripodText,
  isCuripodText,
} from '../../components/SlideComponents/slateUtil';
import renderParagraphLine from './renderParagraphLine';
import { Alignment } from './renderTextElement';
import { setPdfTextStyling } from './renderUtils';

type RenderParagraphProps = {
  pdf: jsPDF;
  paraElement: CuripodElement;
  x: number;
  y: number;
  width: number;
  scale: number;
};

const renderParagraph = async ({
  pdf,
  x,
  y,
  width,
  paraElement,
  scale,
}: RenderParagraphProps) => {
  const alignment = (paraElement.align as Alignment) || 'left';
  const currentX = x;
  let currentY = y;
  if (!paraElement.children || !Array.isArray(paraElement.children))
    return { updatedY: currentY };

  // combine all para children as they are part of same line
  const paraWords: CuripodText[] = [];

  //split words if they are too long to fit in width
  const handleWordSplit = (word: CuripodText): CuripodText[] => {
    setPdfTextStyling({
      pdf,
      color: word.color || '#000',
      bold: !!word.bold,
      italic: !!word.italic,
    });
    const textWidth = pdf.getTextDimensions(word.text).w;
    if (textWidth > width) {
      let firstPartText = '';
      for (const char of word.text) {
        const subTextWidth = firstPartText
          ? pdf.getTextDimensions(firstPartText + char).w
          : 0;
        if (subTextWidth > width) {
          break;
        } else {
          firstPartText = firstPartText + char;
        }
      }

      const fittingText = firstPartText;
      const nonFittingText = word.text.substring(firstPartText.length);

      return [
        ...handleWordSplit({ ...word, text: fittingText }),
        ...handleWordSplit({ ...word, text: nonFittingText }),
      ];
    } else {
      return [word];
    }
  };

  for (const eachParaChild of paraElement.children as CuripodText[]) {
    // Skip if text is undefined or null
    if (!eachParaChild.text) continue;

    const words = eachParaChild.text.split(/(\s+)/).flatMap(word => {
      return word.split(/(\n)/).filter(Boolean);
    });
    for (const eachWord of words) {
      const sizeHandledWords = handleWordSplit({
        ...eachParaChild, //keeps rest of styling properties
        text: eachWord,
      });
      paraWords.push(...sizeHandledWords);
    }
  }

  const allLines: CuripodText[][] = [];
  let currentLine: CuripodText[] = [];
  let currentLineWidth = 0;

  for (const eachParaWord of paraWords) {
    const textWidth = pdf.getTextDimensions(eachParaWord.text).w;
    if (eachParaWord.text === '\n') {
      allLines.push(currentLine);
      currentLine = [];
      currentLineWidth = 0;
    } else if (currentLineWidth + textWidth > width) {
      allLines.push(currentLine);
      currentLine = [eachParaWord];
      currentLineWidth = textWidth;
    } else {
      currentLine.push(eachParaWord);
      currentLineWidth += textWidth;
    }
  }
  if (currentLine.length) allLines.push(currentLine);

  for (const eachLine of allLines) {
    const { updatedY } = await renderParagraphLine({
      pdf,
      x: currentX,
      y: currentY,
      width,
      alignment,
      paraLine: eachLine,
      scale,
    });
    currentY = updatedY;
  }

  return { updatedY: currentY };
};

export default renderParagraph;
